import React, { useId, useState } from "react";
import styled from "@emotion/styled";
import { bodyStyle } from "./Text";
import { text, surface, fontSize, spacing, lineHeight } from "@atlas-int/tokens";

export const borderColor = text.muted;

const LargeTerm = styled.dt`
  font-size: ${fontSize["3xl"]};
  line-height: 1.14;
  color: ${text.primary};
  border-top: 1px solid ${borderColor};
  background-color: ${surface.subtle};
  padding: ${spacing[8]};
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background-color: ${surface.subtleHover};
    cursor: pointer;
  }
`;
const SmallTerm = styled(LargeTerm)`
  font-size: ${fontSize.lg};
  padding: ${spacing[6]};
  line-height: ${spacing[6]};
`;

const Definition = styled.dd`
  transition: height 200ms ease-in-out;
  overflow: hidden;
  box-sizing: border-box;

  a {
    color: ${text.content};

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ExplanationContent = styled.div`
  ${bodyStyle}
  padding: ${spacing[8]};
  border-top: 1px solid ${borderColor};
  margin-bottom: 0;
`;

const Icon = styled.div`
  font-size: 2rem;
`;

// ─── HTML sanitizer ───────────────────────────────────────────────────────────

const ALLOWED_TAGS = new Set([
  "a", "b", "br", "code", "div", "em", "h1", "h2", "h3", "h4", "h5", "h6",
  "i", "li", "ol", "p", "pre", "small", "span", "strong", "sub", "sup",
  "table", "tbody", "td", "th", "thead", "tr", "u", "ul",
]);

const ALLOWED_ATTRS = new Set(["href", "target", "rel", "class", "id"]);

/** Strip tags & attributes that are not on the allow list. */
function sanitizeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const walk = (node: Node) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as Element;
        if (!ALLOWED_TAGS.has(el.tagName.toLowerCase())) {
          // Replace disallowed element with its text content
          el.replaceWith(document.createTextNode(el.textContent || ""));
          continue;
        }
        for (const attr of Array.from(el.attributes)) {
          if (!ALLOWED_ATTRS.has(attr.name)) {
            el.removeAttribute(attr.name);
          }
        }
        // Force safe link targets
        if (el.tagName === "A") {
          const href = el.getAttribute("href") || "";
          if (href.startsWith("javascript:")) {
            el.setAttribute("href", "#");
          }
        }
        walk(el);
      }
    }
  };
  walk(doc.body);
  return doc.body.innerHTML;
}

export interface ExpandableItemProps {
  term: string;
  /**
   * Content shown when expanded.
   * - Pass a `ReactNode` (JSX) for safe rendering (preferred).
   * - Pass a `string` for backward-compatible HTML rendering.
   *   HTML strings are sanitized to allow only safe markup.
   */
  definition: string | React.ReactNode;
  smallLabel?: boolean;
}

const ExpandableItem = (props: ExpandableItemProps) => {
  const { term, definition, smallLabel } = props;

  const [isExpanded, setIsExpanded] = useState(false);
  const contentId = useId();

  const toggleDefinition = () => {
    setIsExpanded((prev) => !prev);
  };

  const Term = smallLabel === true ? SmallTerm : LargeTerm;

  return (
    <>
      <Term
        onClick={toggleDefinition}
        aria-expanded={isExpanded}
        aria-controls={contentId}
      >
        {term}
        <Icon>{isExpanded ? "-" : "+"}</Icon>
      </Term>
      <Definition
        style={{ height: isExpanded ? "auto" : 0 }}
        id={contentId}
        role="region"
      >
        <ExplanationContent>
          {typeof definition === "string" ? (
            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(definition) }} />
          ) : (
            definition
          )}
        </ExplanationContent>
      </Definition>
    </>
  );
};

export default ExpandableItem;
